import { useState, useRef, useEffect } from 'react';
import { commentsApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import { MessageSquare, X, Send, Trash2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'por-fase', label: 'Por fase' },
];

const STATUSES = [
  { value: 'pending',     label: 'Recibido',    color: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
  { value: 'in_progress', label: 'En Progreso', color: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  { value: 'done',        label: 'Hecho',       color: 'bg-green-500/15 text-green-400 border-green-500/30' },
  { value: 'rejected',    label: 'Rechazado',   color: 'bg-red-500/15 text-red-400 border-red-500/30' },
];

const getStatus = (value) => STATUSES.find(s => s.value === value) || STATUSES[0];

const StatusBadge = ({ status }) => {
  const s = getStatus(status);
  return (
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${s.color}`}>
      {s.label}
    </span>
  );
};

const StatusSelector = ({ currentStatus, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const s = getStatus(currentStatus);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border ${s.color} hover:opacity-80 transition-opacity`}
      >
        {s.label}
        <ChevronDown className="w-2.5 h-2.5" />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 bg-card border border-border rounded-md shadow-lg py-1 min-w-[110px]">
          {STATUSES.map(st => (
            <button
              key={st.value}
              type="button"
              className={`w-full text-left text-xs px-3 py-1.5 hover:bg-muted transition-colors ${st.value === currentStatus ? 'font-semibold' : ''}`}
              onClick={() => { onChange(st.value); setOpen(false); }}
            >
              {st.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// CommentItem is defined OUTSIDE CommentsPanel so React doesn't remount it on every re-render
const CommentItem = ({ comment, currentUserId, isOrganizer, onStatusChange, onDelete }) => (
  <div className="group flex gap-3 py-3 border-b border-border last:border-0">
    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 text-sm font-medium">
      {comment.user_name?.[0]?.toUpperCase() || '?'}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <span className="text-sm font-medium truncate">{comment.user_name}</span>
        {comment.user_role === 'client' && (
          <span className="text-xs bg-blue-500/10 text-blue-400 px-1.5 py-0.5 rounded">Cliente</span>
        )}
        <span className="text-xs text-muted-foreground ml-auto">
          {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true, locale: es })}
        </span>
      </div>
      <p className="text-sm text-muted-foreground whitespace-pre-wrap break-words mb-2">{comment.content}</p>
      {/* Status row */}
      <div className="flex items-center gap-2">
        {isOrganizer ? (
          <StatusSelector
            currentStatus={comment.status || 'pending'}
            onChange={(status) => onStatusChange(comment.id, status)}
          />
        ) : (
          <StatusBadge status={comment.status || 'pending'} />
        )}
      </div>
    </div>
    {comment.user_id === currentUserId && (
      <Button
        variant="ghost"
        size="icon"
        className="opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7 flex-shrink-0"
        onClick={() => onDelete(comment.id)}
      >
        <Trash2 className="w-3.5 h-3.5 text-destructive" />
      </Button>
    )}
  </div>
);

export default function CommentsPanel({ eventId, phases = [], comments = [], onCommentsChange, onClose, floating = false, highlightPhaseId = null, isOrganizer = false }) {
  const { user } = useAuth();
  const [tab, setTab] = useState('general');
  const [content, setContent] = useState('');
  const [selectedPhaseId, setSelectedPhaseId] = useState(null);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (highlightPhaseId) {
      setTab('por-fase');
      setSelectedPhaseId(String(highlightPhaseId));
    }
  }, [highlightPhaseId]);

  const generalComments = comments.filter(c => !c.phase_id);
  const phaseComments = comments.filter(c => !!c.phase_id);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [comments.length]);

  const handleSend = async () => {
    if (!content.trim()) return;
    setSending(true);
    try {
      let res;
      if (tab === 'por-fase' && selectedPhaseId) {
        res = await commentsApi.createForPhase(eventId, selectedPhaseId, content.trim());
      } else {
        res = await commentsApi.createForEvent(eventId, content.trim());
      }
      onCommentsChange(prev => [...prev, res.data]);
      setContent('');
    } catch {
      toast.error('Error al enviar comentario');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (commentId) => {
    try {
      await commentsApi.delete(eventId, commentId);
      onCommentsChange(prev => prev.filter(c => c.id !== commentId));
    } catch {
      toast.error('Error al borrar comentario');
    }
  };

  const handleStatusChange = async (commentId, status) => {
    try {
      const res = await commentsApi.updateStatus(eventId, commentId, status);
      onCommentsChange(prev => prev.map(c => c.id === commentId ? res.data : c));
    } catch {
      toast.error('Error al actualizar estado');
    }
  };

  const panelClass = floating
    ? 'fixed right-2 md:right-4 top-24 md:top-16 bottom-4 w-[calc(100vw-1rem)] sm:w-80 z-40 bg-card border border-border rounded-lg shadow-xl flex flex-col'
    : 'flex flex-col h-full';

  return (
    <div className={panelClass}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4" />
          <span className="font-medium text-sm">Comentarios</span>
          {comments.length > 0 && (
            <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">{comments.length}</span>
          )}
        </div>
        {onClose && (
          <Button variant="ghost" size="icon" onClick={onClose} className="h-7 w-7">
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border flex-shrink-0">
        {TABS.map(t => (
          <button
            key={t.id}
            className={`flex-1 text-sm py-2 transition-colors ${tab === t.id ? 'text-primary border-b-2 border-primary font-medium' : 'text-muted-foreground hover:text-foreground'}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Comment list */}
      <ScrollArea className="flex-1 px-4">
        {tab === 'general' ? (
          generalComments.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Sin comentarios generales</div>
          ) : (
            generalComments.map(c => (
              <CommentItem
                key={c.id}
                comment={c}
                currentUserId={user?.id}
                isOrganizer={isOrganizer}
                onStatusChange={handleStatusChange}
                onDelete={handleDelete}
              />
            ))
          )
        ) : (() => {
          if (!selectedPhaseId) {
            return (
              <div className="py-8 text-center text-sm text-muted-foreground">
                Selecciona una fase en el selector de abajo para ver sus comentarios
              </div>
            );
          }
          const phase = phases.find(p => String(p.id) === String(selectedPhaseId));
          if (!phase) return null;
          const pc = phaseComments.filter(c => c.phase_id === phase.id);
          return (
            <div>
              <div className="flex items-center gap-2 py-2 border-b border-border mb-2">
                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: phase.color }} />
                <span className="text-xs font-semibold truncate">{phase.name}</span>
                <span className="text-xs text-muted-foreground ml-auto">{pc.length} comentario{pc.length !== 1 ? 's' : ''}</span>
              </div>
              {pc.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">Sin comentarios en esta fase</p>
              ) : (
                pc.map(c => (
                  <CommentItem
                    key={c.id}
                    comment={c}
                    currentUserId={user?.id}
                    isOrganizer={isOrganizer}
                    onStatusChange={handleStatusChange}
                    onDelete={handleDelete}
                  />
                ))
              )}
            </div>
          );
        })()}
        <div ref={bottomRef} />
      </ScrollArea>

      {/* Input */}
      <div className="p-4 border-t border-border flex-shrink-0 space-y-2">
        {tab === 'por-fase' && (
          <select
            className="w-full text-sm bg-background border border-border rounded px-2 py-1.5 text-foreground"
            value={selectedPhaseId || ''}
            onChange={e => setSelectedPhaseId(e.target.value || null)}
          >
            <option value="">Selecciona una fase...</option>
            {phases.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        )}
        <div className="flex gap-2">
          <Textarea
            placeholder="Escribe un comentario..."
            value={content}
            onChange={e => setContent(e.target.value)}
            className="text-sm min-h-[60px] resize-none"
            onKeyDown={e => {
              if (e.key === 'Enter' && e.ctrlKey) handleSend();
            }}
          />
          <Button
            size="icon"
            onClick={handleSend}
            disabled={sending || !content.trim() || (tab === 'por-fase' && !selectedPhaseId)}
            className="flex-shrink-0 self-end"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">Ctrl+Enter para enviar</p>
      </div>
    </div>
  );
}
